import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import ClientHeader from "../components/ClientHeader";
import NavSidebar from "../components/NavSidebar";
import { usePageMeta } from "../components/DashboardShell";
import { useView } from "../contexts/ViewContext";
import { fmtDate, fmtCount } from "../components/shared/format";
import ThinScroll from "../components/shared/ThinScroll";
import InfoTip from "../components/shared/InfoTip";
import { MetricCardShell, MetricSkeleton, MetricError, TONE_STYLES } from "../components/shared/MetricCardShell";
import VerdictCard from "../components/event-intelligence/VerdictCard";
import EventTimelinePanel from "../components/event-intelligence/EventTimelinePanel";
import WeeklyDensityPanel from "../components/event-intelligence/WeeklyDensityPanel";
import OpportunityBoardPanel from "../components/event-intelligence/OpportunityBoardPanel";
import PlaybookGalleryPanel from "../components/event-intelligence/PlaybookGalleryPanel";
import CategoryAffinityPanel from "../components/event-intelligence/CategoryAffinityPanel";
import PropertyEventMatchPanel from "../components/event-intelligence/PropertyEventMatchPanel";
import SourceComparisonPanel from "../components/event-intelligence/SourceComparisonPanel";
import CategoryDistributionPanel from "../components/event-intelligence/CategoryDistributionPanel";
import PipelineHealthPanel from "../components/event-intelligence/PipelineHealthPanel";
import PlaybookDetailSlideout from "../components/event-intelligence/PlaybookDetailSlideout";
import { parseLocalDate, startOfTodayMs } from "../components/event-intelligence/shared";
import {
  getEiEcp, getEiAmi, getEiMarketingVelocity, getEiPlaybookCoverage, getEiSourceDiversity,
  getEiTimeline, getEiWeeklyDensity, getEiOpportunities, getEiPlaybooks, getEiCategoryAffinity,
  getEiPropertyEventMatch, getEiSourceComparison, getEiCategoryDistribution, getEiPipelineHealth,
} from "../api";

const TABS = [
  { key: "opportunities", label: "Opportunity Board" },
  { key: "playbooks", label: "Playbook Inbox" },
  { key: "affinity", label: "Affinity Analysis" },
  { key: "pipeline", label: "Sources" },
];

// fmtNum was a byte-identical local copy of the shared `fmtCount`
// (Number(v || 0).toLocaleString()) — now imported from shared/format.js.
// fmtPct — INPUT IS ALREADY A PERCENT: 27 → "27%" (default 0 decimals; pass
// `digits` for more). EI KPI values (ami, coverage pct) arrive percent-valued,
// so this does NOT multiply by 100, and it renders missing → "0%" (not "—")
// because these tiles always show a number.
//
// ⚠️ CONTRACT TRAP: NOT the same as the RATIO-input `fmtPercent` in
// src/components/shared/format.js and google-analytics/ (0.27 → "27.00%",
// ×100). Same-looking name, opposite input — a percent (27) through fmtPercent
// renders "2700.00%". The shared percent-input equivalent is `fmtPctValue`.
const fmtPct = (v, digits = 0) => `${Number(v || 0).toFixed(digits)}%`;

// MetricCardShell / TONE_STYLES / ICON_PATHS / MetricSkeleton / MetricError were an
// inlined near-duplicate of CI's copy; both now share one module
// (src/components/shared/MetricCardShell.js), standardized on CI's styling. EI's four
// unique icons (calendar/clock/checklist/share) are carried in the shared union, so
// nothing falls back. Slight visual shift vs the old EI inline: p-6→p-4, watermark
// /10→/25 (darker), footer loses its top border — the CI look, now consistent.

const ecpTone = (v) => (v >= 500 ? "emerald" : v >= 200 ? "amber" : "red");
const amiTone = (v) => (v >= 70 ? "emerald" : v >= 40 ? "amber" : "red");
const velocityTone = (v) => (v >= 40 ? "emerald" : v >= 14 ? "amber" : "red");
const coverageTone = (v) => (v >= 90 ? "emerald" : v >= 70 ? "amber" : "red");
const diversityTone = (v) => (v >= 0.5 ? "emerald" : v >= 0.25 ? "amber" : "red");

const amiLabel = (v) => (v >= 70 ? "Good" : v >= 40 ? "Moderate" : "Mismatch");
const velocityLabel = (v) => (v >= 40 ? "Excellent" : v >= 14 ? "Good" : "Reactive");
const coverageLabel = (v) => (v >= 90 ? "Healthy" : v >= 70 ? "Lagging" : "Behind");
const diversityLabel = (v) => (v >= 0.5 ? "Balanced" : v >= 0.25 ? "Skewed" : "Single-source");

const EcpCard = ({ state, rangeStart, rangeEnd, ecp30, ecp60 }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Event Capture Potential" message={state.error} />;

  const headline = Number(state.data?.ecp || 0);
  const eventsCount = Number(state.data?.events || 0);
  const tone = "brandBlue";
  const s = TONE_STYLES[tone];

  // rangeStart/rangeEnd are plain "YYYY-MM-DD" strings. Parsed with `new Date()`
  // they became midnight UTC while `today` was local midnight, so west of UTC a
  // range starting today compared as earlier — labelling a forward-looking window
  // "Past N days". Both sides are now local midnight.
  const start = parseLocalDate(rangeStart);
  const end = parseLocalDate(rangeEnd);
  const days = start && end ? Math.max(0, Math.round((end - start) / 86400000)) : null;
  const isForward = start ? start.getTime() >= startOfTodayMs() : true;
  const rangeLabel = days == null ? "" : `${isForward ? "Next" : "Past"} ${days} day${days === 1 ? "" : "s"}`;

  // Sub-line: only meaningful when the window is long enough that 30/60 are
  // genuinely "earlier slices" of the selected range. Hide otherwise so we
  // don't repeat or contradict the headline.
  const showBreakdown = days != null && days >= 45 && (ecp30 != null || ecp60 != null);

  return (
    <MetricCardShell
      tone={tone}
      icon="calendar"
      title="Event Capture Potential"
      tooltip="Composite score of upcoming-event demand in your market, weighted by each event's opportunity score. Higher = more marketable demand to chase in your window."
      footer={
        <div className="flex items-center justify-between">
          <span>{fmtCount(eventsCount)} {isForward ? "upcoming" : ""} events</span>
          <span className="text-gray-400 dark:text-gray-500">{rangeLabel}</span>
        </div>
      }
    >
      <div className="mt-2 flex items-baseline gap-2">
        <InfoTip text="Sum of opportunity scores for all upcoming events in the window. First 30d / 60d below are the same sum restricted to those earlier slices.">
          <span className={`text-3xl font-extrabold leading-none ${s.valueClass}`}>{fmtCount(headline)}</span>
        </InfoTip>
        <span className="text-xs text-gray-400 dark:text-gray-500">score</span>
      </div>
      {showBreakdown && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {ecp30 != null && days >= 30 && (
            <>First 30d: <span className="font-semibold text-gray-700 dark:text-gray-300">{fmtCount(Number(ecp30))}</span></>
          )}
          {ecp30 != null && ecp60 != null && days >= 60 && <span className="mx-1">·</span>}
          {ecp60 != null && days >= 60 && (
            <>First 60d: <span className="font-semibold text-gray-700 dark:text-gray-300">{fmtCount(Number(ecp60))}</span></>
          )}
        </div>
      )}
    </MetricCardShell>
  );
};

const AmiCard = ({ state }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Accommodation Match" message={state.error} />;

  const ami = Math.max(0, Math.min(100, Number(state.data?.ami || 0)));
  const tone = amiTone(ami);
  const s = TONE_STYLES[tone];

  return (
    <MetricCardShell
      tone={tone}
      icon="target"
      title="Accommodation Match"
      tooltip="Share of high-value local events whose typical attendees match your site mix (RV, cabin, glamping, tent). Above 70% means your inventory fits the demand."
      footer="of high-value events match your site types"
    >
      <div className="mt-2 flex items-baseline gap-2">
        <InfoTip text="Of the events worth marketing to, how many call for the kind of accommodations you actually offer. Shown as a percentage.">
          <span className={`text-3xl font-extrabold leading-none ${s.valueClass}`}>{fmtPct(ami)}</span>
        </InfoTip>
        <span className={`text-xs font-semibold ${s.valueClass}`}>{amiLabel(ami)}</span>
      </div>
      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mt-3">
        <div className={`h-full ${s.barFill}`} style={{ width: `${ami}%` }} />
      </div>
    </MetricCardShell>
  );
};

const MarketingVelocityCard = ({ state }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Marketing Velocity" message={state.error} />;

  const avg = Number(state.data?.avg_lead_days || 0);
  const min = Number(state.data?.min_lead || 0);
  const max = Number(state.data?.max_lead || 0);
  const tone = velocityTone(avg);
  const s = TONE_STYLES[tone];

  return (
    <MetricCardShell
      tone={tone}
      icon="clock"
      title="Marketing Velocity"
      tooltip="Average lead time between playbook generation and event start. Above 40 days means you're planning well ahead; under 14 days is reactive."
      footer={max > 0 ? `Range: ${fmtCount(min)}–${fmtCount(max)} days` : "No playbooks yet"}
    >
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-3xl font-extrabold leading-none ${s.valueClass}`}>{fmtCount(avg)}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">days avg</span>
      </div>
      <div className={`text-xs font-semibold mt-1.5 ${s.valueClass}`}>
        {velocityLabel(avg)}
      </div>
    </MetricCardShell>
  );
};

const PlaybookCoverageCard = ({ state, urgentEvents = [] }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Playbook Coverage" message={state.error} />;

  const pct = Math.max(0, Math.min(100, Number(state.data?.coverage_pct || 0)));
  const withPb = Number(state.data?.with_playbook || 0);
  const total = Number(state.data?.total_opportunities || 0);
  const tone = coverageTone(pct);
  const s = TONE_STYLES[tone];

  return (
    <MetricCardShell
      tone={tone}
      icon="checklist"
      title="Playbook Coverage"
      tooltip="Share of scored opportunities that already have a marketing playbook attached. Below 70% means you have unresourced demand sitting on the table."
      footer={total > 0 ? `${fmtCount(withPb)} of ${fmtCount(total)} opportunities` : "No opportunities yet"}
    >
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`text-3xl font-extrabold leading-none ${s.valueClass}`}>{fmtPct(pct)}</span>
        <span className={`text-xs font-semibold ${s.valueClass}`}>{coverageLabel(pct)}</span>
      </div>
      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mt-3">
        <div className={`h-full ${s.barFill}`} style={{ width: `${pct}%` }} />
      </div>
      {urgentEvents.length > 0 && (
        <div className="mt-2">
          <PlaybookUrgencyBadge events={urgentEvents} />
        </div>
      )}
    </MetricCardShell>
  );
};

const SourceDiversityCard = ({ state }) => {
  if (state.loading) return <MetricSkeleton />;
  if (state.error) return <MetricError title="Source Diversity" message={state.error} />;

  const ratio = Math.max(0, Math.min(1, Number(state.data?.diversity_ratio || 0)));
  const tm = Number(state.data?.tm_count || 0);
  const serp = Number(state.data?.serp_count || 0);
  const total = tm + serp;
  const tmPct = total > 0 ? (tm / total) * 100 : 0;
  const serpPct = total > 0 ? (serp / total) * 100 : 0;
  const tone = diversityTone(ratio);
  const s = TONE_STYLES[tone];

  return (
    <MetricCardShell
      tone={tone}
      icon="share"
      title="Source Diversity"
      tooltip="How balanced your event pipeline is between Ticketmaster and Google sources. Above 0.5 is balanced; under 0.25 means you're relying on one source."
      footer={
        <div className="flex items-center justify-between">
          <span><span className="inline-block w-2 h-2 rounded-sm bg-[#009CDE] mr-1 align-middle" />TM {fmtCount(tm)}</span>
          <span><span className="inline-block w-2 h-2 rounded-sm bg-[#4285F4] mr-1 align-middle" />Google {fmtCount(serp)}</span>
        </div>
      }
    >
      <div className="mt-2 flex items-baseline gap-2">
        <InfoTip text="The smaller source count divided by the larger one. 1.0 means both sources contribute equally; 0 means everything comes from a single source.">
          <span className={`text-3xl font-extrabold leading-none ${s.valueClass}`}>{ratio.toFixed(2)}</span>
        </InfoTip>
        <span className={`text-xs font-semibold ${s.valueClass}`}>{diversityLabel(ratio)}</span>
      </div>
      <div className="flex w-full h-1.5 rounded-full overflow-hidden mt-3 bg-gray-100 dark:bg-gray-800">
        <div className="h-full bg-[#009CDE]" style={{ width: `${tmPct}%` }} />
        <div className="h-full bg-[#4285F4]" style={{ width: `${serpPct}%` }} />
      </div>
    </MetricCardShell>
  );
};

const initialState = { loading: false, error: "", data: null };

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const LastUpdatedIndicator = ({ iso }) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const datePart = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const timePart = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  const formatted = `${datePart} at ${timePart}`;
  return (
    <span className="text-[11px] text-gray-400 dark:text-gray-500">
      Last updated: {formatted}
    </span>
  );
};

const AllTimeToggle = ({ active, onToggle }) => (
  <div className="inline-flex items-center gap-2">
    <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
      Range
    </span>
    <div className="inline-flex items-center">
      <button
        type="button"
        onClick={() => onToggle(false)}
        title="Limit to the date range selected in the header"
        className={`px-2 py-0.5 text-[11px] rounded-l-md border ${
          !active
            ? "bg-[#32B1FF] text-white border-[#32B1FF]"
            : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
        }`}
      >
        Selected
      </button>
      <button
        type="button"
        onClick={() => onToggle(true)}
        title="Include all events, ignoring the header date range"
        className={`px-2 py-0.5 text-[11px] rounded-r-md border -ml-px ${
          active
            ? "bg-[#32B1FF] text-white border-[#32B1FF]"
            : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
        }`}
      >
        All-Time
      </button>
    </div>
  </div>
);

const PlaybookUrgencyBadge = ({ events }) => {
  if (!events?.length) return null;
  const previewLines = events
    .slice(0, 5)
    .map((e) => `• ${e.event_name} — score ${Number(e.opportunity_score || 0)}, ${Number(e.lead_time_days || 0)}d lead`)
    .join("\n");
  const remaining = events.length - Math.min(5, events.length);
  const tooltip =
    `${events.length} high-value event${events.length === 1 ? "" : "s"} ` +
    `need${events.length === 1 ? "s" : ""} a playbook (score ≥ 50, lead time < 14 days):\n` +
    previewLines +
    (remaining > 0 ? `\n+${remaining} more` : "");
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400"
      title={tooltip}
    >
      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      {events.length} need{events.length === 1 ? "s" : ""} playbook{events.length === 1 ? "" : "s"} in &lt;14d
    </span>
  );
};

const EventIntelligencePage = () => {
  usePageMeta({ headerActive: 'Events', sidebarActive: 'events' });
  const username = localStorage.getItem("username") || "Admin";
  const view = useView();
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  const isClient = view.isClient;
  const clientId = view.supabaseClientId;

  // Tab + timeline-filter state lives in the URL for shareable links (spec
  // event_intelligence.md:998-1001). Mirrors the RouteIntelligencePage pattern:
  // an updateParam(key, value) helper that drops empty values from the query.
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === "") next.delete(key);
      else next.set(key, String(value));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const TAB_KEYS = useMemo(() => TABS.map((tab) => tab.key), []);
  const tabParam = searchParams.get("tab");
  const activeTab = TAB_KEYS.includes(tabParam) ? tabParam : "opportunities";
  const setActiveTab = useCallback((tab) => updateParam("tab", tab), [updateParam]);

  const { rangeStart, rangeEnd, setDateRange } = view;

  // EI is forward-looking: default the global range to "next 90 days" on entry
  // (spec line 20). Fixes the "This Month" open (migration review #7).
  //
  // IMPORTANT (race fix): date-scoped loaders below gate on `rangeReady`, which is
  // only true once this override has landed in ViewContext. Until then the global
  // range still holds ViewContext's "This Month" default — so without this gate the
  // first fetch went out for This Month while the header already showed the next-90
  // window, and the displayed data didn't match the label. Holding fetches until the
  // override lands keeps the header and the data on the same range.
  const appliedDefaultRange = useRef(false);
  const [rangeOverridden, setRangeOverridden] = useState(false);
  useEffect(() => {
    if (appliedDefaultRange.current) return;
    appliedDefaultRange.current = true;
    const today = new Date();
    setDateRange(fmtDate(today), fmtDate(addDays(today, 90)), "custom");
    setRangeOverridden(true);
  }, [setDateRange]);

  // Date-scoped fetches may run once our default has landed (or the user changed the
  // range). Guards against the stale "This Month" first render.
  const rangeReady = rangeOverridden && !!rangeStart && !!rangeEnd;

  // Per-section "All-time" overrides for endpoints whose backend default is all-time.
  // When true, the loader sends empty start/end so the backend returns the full history.
  const [oppsAllTime, setOppsAllTime] = useState(false);
  const [sourceCompAllTime, setSourceCompAllTime] = useState(false);
  const [catDistAllTime, setCatDistAllTime] = useState(false);

  const [ecp, setEcp] = useState(initialState);
  const [ami, setAmi] = useState(initialState);
  const [velocity, setVelocity] = useState(initialState);
  const [coverage, setCoverage] = useState(initialState);
  const [diversity, setDiversity] = useState(initialState);
  const [timeline, setTimeline] = useState(initialState);
  const [weeklyDensity, setWeeklyDensity] = useState(initialState);
  const [opportunities, setOpportunities] = useState(initialState);
  const [playbooks, setPlaybooks] = useState(initialState);
  const [categoryAffinity, setCategoryAffinity] = useState(initialState);
  const [propertyEventMatch, setPropertyEventMatch] = useState(initialState);
  const [sourceComparison, setSourceComparison] = useState(initialState);
  const [categoryDistribution, setCategoryDistribution] = useState(initialState);
  const [pipelineHealth, setPipelineHealth] = useState(initialState);
  const [slideoutPlaybook, setSlideoutPlaybook] = useState(null);
  // Set when a no-playbook Timeline card is clicked — Board panel watches this
  // to expand the matching row and scroll to it. The nonce lets re-clicking the
  // same card re-fire the effect (a monotonic ref, not Date.now(), so two clicks
  // in the same millisecond still register as distinct focus requests).
  const [oppFocus, setOppFocus] = useState(null);
  const oppFocusNonce = useRef(0);
  const tabsRef = useRef(null);

  const playbookList = Array.isArray(playbooks.data) ? playbooks.data : [];

  const urgentEvents = useMemo(() => {
    const opps = Array.isArray(opportunities.data) ? opportunities.data : [];
    return opps
      .filter((o) => {
        const score = Number(o.opportunity_score || 0);
        const days = Number(o.lead_time_days);
        if (!Number.isFinite(days)) return false;
        return score >= 50 && days >= 0 && days < 14 && !o.has_playbook;
      })
      .sort((a, b) => Number(a.lead_time_days || 0) - Number(b.lead_time_days || 0));
  }, [opportunities.data]);

  const deadCategories = useMemo(() => {
    const aff = Array.isArray(categoryAffinity.data) ? categoryAffinity.data : [];
    const dist = Array.isArray(categoryDistribution.data) ? categoryDistribution.data : [];
    if (aff.length === 0) return [];

    const seen = new Set(
      dist
        .map((d) => (typeof d.category === "string" ? d.category.toLowerCase().trim() : ""))
        .filter(Boolean)
    );

    // Roll up affinity rows to one entry per top-level category, keeping the
    // best accommodation match across subcategories.
    const byCategory = new Map();
    aff.forEach((a) => {
      const key = typeof a.category === "string" ? a.category.toLowerCase().trim() : "";
      if (!key) return;
      const accomScores = {
        rv: Number(a.rv_affinity || 0),
        cabin: Number(a.cabin_affinity || 0),
        glamping: Number(a.glamping_affinity || 0),
        tent: Number(a.tent_affinity || 0),
      };
      const best = Object.entries(accomScores).reduce(
        (acc, [k, v]) => (v > acc.score ? { type: k, score: v } : acc),
        { type: null, score: 0 }
      );
      const prev = byCategory.get(key);
      if (!prev || best.score > prev.maxAffinity) {
        byCategory.set(key, {
          category: a.category,
          maxAffinity: best.score,
          bestAccommodation: best.type,
        });
      }
    });

    return Array.from(byCategory.values())
      .filter((c) => c.maxAffinity >= 0.6 && !seen.has(c.category.toLowerCase().trim()))
      .sort((a, b) => b.maxAffinity - a.maxAffinity);
  }, [categoryAffinity.data, categoryDistribution.data]);

  // Match an event/opportunity row to its playbook by id, falling back to name.
  // Shared by the timeline card click and the Opportunity Board's in-breakdown
  // "View playbook" link so both resolve playbooks identically.
  const findPlaybookForEvent = useCallback((event) => {
    if (!event) return null;
    return playbookList.find((playbook) =>
      (event.event_id && playbook.event_id && playbook.event_id === event.event_id) ||
      (event.event_name && playbook.event_name && playbook.event_name === event.event_name)
    ) || null;
  }, [playbookList]);

  // Opportunity Board in-breakdown playbook link (spec event_intelligence.md:636):
  // the expanded score breakdown carries a link to the playbook when one exists.
  // Opens the same slideout the timeline does and encodes it in the URL so it's
  // shareable, mirroring the timeline path.
  const handleOpenPlaybook = useCallback((row) => {
    const match = findPlaybookForEvent(row);
    if (!match) return;
    if (row && row.event_id) updateParam("event", row.event_id);
    setSlideoutPlaybook(match);
  }, [findPlaybookForEvent, updateParam]);

  // Timeline card click (spec event_intelligence.md:545): open the event's playbook
  // if it has one, otherwise surface the opportunity detail — implemented as the
  // Opportunity Board's score breakdown. No random-playbook fallback: if has_playbook
  // but no row matches, we still open the opportunity detail rather than a wrong
  // playbook.
  //
  // `fromUrl` is true when this is replaying a `?event=ID` deep-link on load — in that
  // case we must NOT write the param back (the effect below already owns it and a
  // re-write would loop) and we skip the smooth scroll (nothing for the user to follow
  // on a fresh load).
  const handleTimelineCardClick = useCallback((event, { fromUrl = false } = {}) => {
    if (!event) return;
    const match = findPlaybookForEvent(event);
    // Shareable deep-link (spec event_intelligence.md:1001): the open event/playbook
    // is encoded as ?event=ID.
    if (!fromUrl && event.event_id) updateParam("event", event.event_id);
    if (event.has_playbook && match) {
      setSlideoutPlaybook(match);
      // Deep-link with both ?tab= and ?event= (e.g. ?tab=affinity&event=ID): the
      // slideout is an overlay, so the requested tab stays meaningful as the backdrop.
      // Scroll it into view once on load so closing the slideout leaves the user on the
      // tab they linked to (not stranded at the top). User-initiated clicks (fromUrl
      // false) don't need this — the strip is already in view.
      if (fromUrl) {
        const initialTab = searchParams.get("tab");
        if (initialTab && initialTab !== "opportunities" && TAB_KEYS.includes(initialTab)) {
          requestAnimationFrame(() => {
            tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      }
      return;
    }
    // No playbook (or none found) → jump to the Opportunity Board and expand this event.
    oppFocusNonce.current += 1;
    setOppFocus({ event_id: event.event_id, event_name: event.event_name, nonce: oppFocusNonce.current });
    setActiveTab("opportunities");
    if (!fromUrl) {
      requestAnimationFrame(() => {
        tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [findPlaybookForEvent, updateParam, setActiveTab, searchParams, TAB_KEYS]);

  // Deep-link replay (spec event_intelligence.md:1001): when the URL carries ?event=ID
  // — on first load or a shared link — replay the same routing a card click would do,
  // once the timeline has loaded. Guarded by a ref so it fires only for a given id and
  // doesn't fight the user if they navigate away afterward.
  const replayedEventParam = useRef(null);
  const eventParam = searchParams.get("event");
  const timelineEvents = Array.isArray(timeline.data) ? timeline.data : [];
  useEffect(() => {
    if (!eventParam) { replayedEventParam.current = null; return; }
    if (replayedEventParam.current === eventParam) return;
    if (timeline.loading || timelineEvents.length === 0) return;
    // event_id is numeric in the data but searchParams always yields a string, so compare
    // as strings — a `2948 === "2948"` strict-equality miss is what silently swallowed the
    // deep-link and left the slideout closed.
    const target = timelineEvents.find((event) => String(event.event_id) === eventParam);
    if (!target) return; // id not in the current timeline (filtered range / stale link) — leave it be
    // Wait for the playbooks list too when the target has a playbook: handleTimelineCardClick
    // resolves the playbook via findPlaybookForEvent(playbookList). If we fire before playbooks
    // load, the match comes back empty and the has-playbook event wrongly falls through to the
    // opportunity-detail path with no slideout. Don't commit (set the guard) until we can resolve.
    if (target.has_playbook && playbooks.loading) return;
    replayedEventParam.current = eventParam;
    handleTimelineCardClick(target, { fromUrl: true });
  }, [eventParam, timeline.loading, timelineEvents, playbooks.loading, handleTimelineCardClick]);

  // Deep-link scroll-on-load: when the page is opened on a non-default ?tab= (a shared
  // link to Playbooks / Affinity / Sources), bring the tab strip into view so the user
  // lands ON the linked content instead of at the top above the always-visible Timeline.
  // Fires once on entry only — clicking tabs afterward must NOT yank the page, since the
  // strip is already where the user's cursor is. The ?event= path scrolls on its own.
  const scrolledToTabOnLoad = useRef(false);
  useEffect(() => {
    if (scrolledToTabOnLoad.current) return;
    scrolledToTabOnLoad.current = true;
    const initialTab = searchParams.get("tab");
    if (!initialTab || initialTab === "opportunities") return; // default tab is near the top
    if (!TAB_KEYS.includes(initialTab)) return;
    if (searchParams.get("event")) return; // event deep-link owns the scroll on that path
    // Defer until the tab content has mounted under the strip.
    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    // Mount-only by design: reads the initial URL once via the scrolledToTabOnLoad
    // guard, so it must not re-run on later searchParams/tab changes.
  }, []); // eslint-disable-line

  // Endpoints that ignore dates entirely — playbook gallery, global affinity reference,
  // cumulative pipeline counts. Refire only when the client changes.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    const runUnscoped = (setter, loader) => {
      setter({ loading: true, error: "", data: null });
      loader(clientId)
        .then((resp) => { if (!cancelled) setter({ loading: false, error: "", data: resp?.data ?? null }); })
        .catch((e) => { if (!cancelled) setter({ loading: false, error: e?.message || "Failed to load", data: null }); });
    };

    runUnscoped(setCategoryAffinity, getEiCategoryAffinity);
    runUnscoped(setPipelineHealth, getEiPipelineHealth);

    return () => { cancelled = true; };
  }, [clientId]);

  // Date-scoped endpoints whose behavior is fixed (always follow the picker).
  // Toggling a per-section "All-Time" flag should NOT refetch these.
  useEffect(() => {
    if (!clientId || !rangeReady) return;
    let cancelled = false;

    const runScoped = (setter, loader) => {
      setter({ loading: true, error: "", data: null });
      loader(clientId, rangeStart, rangeEnd)
        .then((resp) => { if (!cancelled) setter({ loading: false, error: "", data: resp?.data ?? null }); })
        .catch((e) => { if (!cancelled) setter({ loading: false, error: e?.message || "Failed to load", data: null }); });
    };

    runScoped(setEcp, getEiEcp);
    runScoped(setAmi, getEiAmi);
    runScoped(setCoverage, getEiPlaybookCoverage);
    runScoped(setVelocity, getEiMarketingVelocity);
    runScoped(setDiversity, getEiSourceDiversity);
    runScoped(setTimeline, getEiTimeline);
    runScoped(setWeeklyDensity, getEiWeeklyDensity);
    runScoped(setPropertyEventMatch, getEiPropertyEventMatch);

    return () => { cancelled = true; };
  }, [clientId, rangeStart, rangeEnd, rangeReady]);

  // Playbook Inbox is always all-time — users want the full backlog ranked
  // by score, not playbooks clipped to the picker window.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setPlaybooks({ loading: true, error: "", data: null });
    getEiPlaybooks(clientId, "", "")
      .then((resp) => { if (!cancelled) setPlaybooks({ loading: false, error: "", data: resp?.data ?? null }); })
      .catch((e) => { if (!cancelled) setPlaybooks({ loading: false, error: e?.message || "Failed to load", data: null }); });
    return () => { cancelled = true; };
  }, [clientId]);

  // Per-section overridable loaders. Each depends only on its own flag so that
  // toggling one section never refetches the others.
  // When not in All-Time mode the loader uses the picker range, so it must wait for
  // the default override to land (rangeReady) — same race guard as the scoped loaders.
  const fetchOverridable = (setter, loader, allTime) => {
    if (!clientId || (!allTime && !rangeReady)) return () => {};
    let cancelled = false;
    setter({ loading: true, error: "", data: null });
    const start = allTime ? "" : rangeStart;
    const end = allTime ? "" : rangeEnd;
    loader(clientId, start, end)
      .then((resp) => { if (!cancelled) setter({ loading: false, error: "", data: resp?.data ?? null }); })
      .catch((e) => { if (!cancelled) setter({ loading: false, error: e?.message || "Failed to load", data: null }); });
    return () => { cancelled = true; };
  };

  useEffect(() => fetchOverridable(setOpportunities, getEiOpportunities, oppsAllTime),
    [clientId, rangeStart, rangeEnd, oppsAllTime, rangeReady]); // eslint-disable-line

  useEffect(() => fetchOverridable(setSourceComparison, getEiSourceComparison, sourceCompAllTime),
    [clientId, rangeStart, rangeEnd, sourceCompAllTime, rangeReady]); // eslint-disable-line

  useEffect(() => fetchOverridable(setCategoryDistribution, getEiCategoryDistribution, catDistAllTime),
    [clientId, rangeStart, rangeEnd, catDistAllTime, rangeReady]); // eslint-disable-line


  return (
    <>
        <main className="flex-1 min-w-0">
          <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">

            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Event Intelligence</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Discover nearby events, score them as marketing opportunities, and generate playbooks.
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <LastUpdatedIndicator iso={ecp.data?.last_scored_at} />
              </div>
            </div>

            {/* Top-level insight card — pipeline-health verdict with AI narrative covering position + signals */}
            <VerdictCard
              ecp={ecp}
              ami={ami}
              velocity={velocity}
              coverage={coverage}
              diversity={diversity}
              urgentCount={urgentEvents.length}
              clientId={clientId}
              startDate={rangeStart}
              endDate={rangeEnd}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <EcpCard
                state={ecp}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                ecp30={ecp?.data?.ecp_30 ?? null}
                ecp60={ecp?.data?.ecp_60 ?? null}
              />
              <AmiCard state={ami} />
              <MarketingVelocityCard state={velocity} />
              <PlaybookCoverageCard state={coverage} urgentEvents={urgentEvents} />
              <SourceDiversityCard state={diversity} />
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
              <WeeklyDensityPanel
                loading={weeklyDensity.loading}
                error={weeklyDensity.error}
                weeks={Array.isArray(weeklyDensity.data) ? weeklyDensity.data : []}
                events={Array.isArray(timeline.data) ? timeline.data : []}
                rangeEnd={rangeEnd}
              />
              <EventTimelinePanel
                loading={timeline.loading}
                error={timeline.error}
                events={Array.isArray(timeline.data) ? timeline.data : []}
                onCardClick={handleTimelineCardClick}
                searchParams={searchParams}
                updateParam={updateParam}
              />
            </div>

            <div ref={tabsRef} className="border-b border-gray-200 dark:border-gray-800 scroll-mt-24">
              <ThinScroll className="flex gap-1 overflow-x-auto pb-1.5 md:pb-0 -mb-px" role="tablist">
                {TABS.map((t) => {
                  const active = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setActiveTab(t.key)}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                        active
                          ? "border-[#32B1FF] text-[#32B1FF]"
                          : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </ThinScroll>
            </div>

            {activeTab === "opportunities" && (
              <OpportunityBoardPanel
                loading={opportunities.loading}
                error={opportunities.error}
                opportunities={Array.isArray(opportunities.data) ? opportunities.data : []}
                rangeControl={<AllTimeToggle active={oppsAllTime} onToggle={setOppsAllTime} />}
                focusRequest={oppFocus}
                onOpenPlaybook={handleOpenPlaybook}
              />
            )}

            {activeTab === "playbooks" && (
              <PlaybookGalleryPanel
                loading={playbooks.loading}
                error={playbooks.error}
                playbooks={playbookList}
              />
            )}

            {activeTab === "affinity" && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <PropertyEventMatchPanel
                    loading={propertyEventMatch.loading}
                    error={propertyEventMatch.error}
                    rows={Array.isArray(propertyEventMatch.data) ? propertyEventMatch.data : []}
                    coverageGaps={deadCategories}
                  />
                  <CategoryAffinityPanel
                    loading={categoryAffinity.loading}
                    error={categoryAffinity.error}
                    rows={Array.isArray(categoryAffinity.data) ? categoryAffinity.data : []}
                  />
                </div>
              </div>
            )}

            {activeTab === "pipeline" && (
              <div className="space-y-6">
                <SourceComparisonPanel
                  loading={sourceComparison.loading}
                  error={sourceComparison.error}
                  rows={Array.isArray(sourceComparison.data) ? sourceComparison.data : []}
                  rangeControl={<AllTimeToggle active={sourceCompAllTime} onToggle={setSourceCompAllTime} />}
                />
                <CategoryDistributionPanel
                  loading={categoryDistribution.loading}
                  error={categoryDistribution.error}
                  rows={Array.isArray(categoryDistribution.data) ? categoryDistribution.data : []}
                  rangeControl={<AllTimeToggle active={catDistAllTime} onToggle={setCatDistAllTime} />}
                />
              </div>
            )}

            <PipelineHealthPanel
              loading={pipelineHealth.loading}
              error={pipelineHealth.error}
              rows={Array.isArray(pipelineHealth.data) ? pipelineHealth.data : []}
            />

          </div>
        </main>

      <PlaybookDetailSlideout
        open={!!slideoutPlaybook}
        playbook={slideoutPlaybook}
        onClose={() => { setSlideoutPlaybook(null); updateParam("event", ""); }}
        onViewAll={() => {
          setSlideoutPlaybook(null);
          updateParam("event", "");
          setActiveTab("playbooks");
          // Wait for the tab content to mount before scrolling.
          requestAnimationFrame(() => {
            tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }}
      />
    </>
  );
};

export default EventIntelligencePage;
